import { motion } from "framer-motion";
import { User, Paperclip, Brain, Copy } from "@phosphor-icons/react";
import JourneyAvatar from "./JourneyAvatar";
import { downloadFileUrl } from "../lib/api";
import { useLongPress } from "../hooks/useLongPress";
import ItemMenu from "./ItemMenu";
import InlineMemoryChip from "./InlineMemoryChip";
import ReminderCreatedChip from "./ReminderCreatedChip";
import TaskDelegatedChip from "./TaskDelegatedChip";
import VisualSentChip from "./VisualSentChip";
import StepGuideCards from "./StepGuideCards";
import LiveDataBadge from "./LiveDataBadge";
import ReminderLookupChip from "./ReminderLookupChip";
import ChipErrorBoundary from "./ChipErrorBoundary";
import { formatChatTimestamp } from "../lib/timeFormat";
import { toast } from "sonner";
import { useRef } from "react";

function FileChip({ fileId }) {
  return (
    <a
      href={downloadFileUrl(fileId)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 mr-1.5 mt-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/80 hover:bg-white/10 transition-colors"
      data-testid={`message-file-${fileId}`}
    >
      <Paperclip size={12} weight="bold" />
      Attachment
    </a>
  );
}

export default function ChatMessage({
  message,
  index,
  onSaveAsMemory,
  inlineSuggestion,
  onInlineResolved,
  remindersCreated,
  tasksDelegated,
  visualsSentToContact,
  stepGuide,
  stepStatus,
  showFace,
  realtimeResults,
  reminderLookup,
  onRegenerateStep,
  tz,
  isStreaming,
}) {
  const isUser = message.role === "user";
  const menuTriggerRef = useRef(null);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const longPress = useLongPress(
    () => onSaveAsMemory?.(message.content),
    { threshold: 500 }
  );

  const menuItems = [
    {
      label: "Save as memory",
      icon: Brain,
      onClick: () => onSaveAsMemory?.(message.content),
      testId: `message-save-memory-${message.id}`,
    },
    {
      label: "Copy text",
      icon: Copy,
      onClick: copyText,
      testId: `message-copy-${message.id}`,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.02, 0.1) }}
      className={`flex gap-3 group ${isUser ? "flex-row-reverse" : "flex-row"}`}
      data-testid={`chat-message-${message.id}`}
    >
      {isUser ? (
        <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
          <User size={14} weight="duotone" className="text-cyan-400" />
        </div>
      ) : (
        <JourneyAvatar size={32} ringed={false} testId="message-avatar-journey" />
      )}
      <div className={`${stepGuide && !isUser ? "max-w-full sm:max-w-[92%] w-full" : "max-w-[82%] sm:max-w-[78%]"} flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div className="relative">
          {(message.content || isUser || !stepGuide) && (
            <>
              <div
                ref={menuTriggerRef}
                {...longPress}
                className={`rounded-2xl px-4 py-3 select-none ${
                  isUser
                    ? "bg-[#121212] border border-white/5 text-white/90"
                    : "bg-[#1a1a1a] border border-white/5"
                }`}
              >
                <div className="md-content text-[15px] whitespace-pre-wrap break-words select-text">
                  {message.content || (isStreaming ? "" : "")}
                </div>
                {message.file_ids && message.file_ids.length > 0 && (
                  <div className="flex flex-wrap pt-1">
                    {message.file_ids.map((id) => (
                      <FileChip key={id} fileId={id} />
                    ))}
                  </div>
                )}
              </div>
              <div
                className={`absolute -top-2 ${
                  isUser ? "-left-2" : "-right-2"
                } opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity`}
              >
                <div className="rounded-md bg-[#0f0f0f] border border-white/10 shadow-md">
                  <ItemMenu
                    items={menuItems}
                    testId={`message-menu-${message.id}`}
                    align={isUser ? "start" : "end"}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {/* Reminder chips for any reminders this assistant message created */}
        {!isUser && remindersCreated && remindersCreated.length > 0 && (
          <ChipErrorBoundary label="reminders-created">
            <div className="mt-1 flex flex-wrap gap-1.5">
              {remindersCreated.map((rem) => (
                <ReminderCreatedChip key={rem.id} reminder={rem} tz={tz} />
              ))}
            </div>
          </ChipErrorBoundary>
        )}
        {/* V9.27 — Delegated-task chips (one per delegate_to_contact tool call). */}
        {!isUser && tasksDelegated && tasksDelegated.length > 0 && (
          <ChipErrorBoundary label="tasks-delegated">
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tasksDelegated.map((t) => (
                <TaskDelegatedChip key={t.id} task={t} />
              ))}
            </div>
          </ChipErrorBoundary>
        )}
        {/* V9.35 — Visual-sent-to-contact chips. */}
        {!isUser && visualsSentToContact && visualsSentToContact.length > 0 && (
          <ChipErrorBoundary label="visuals-sent">
            <div className="mt-1 flex flex-wrap gap-1.5">
              {visualsSentToContact.map((v, i) => (
                <VisualSentChip key={`${v.contact_id || "x"}-${i}`} result={v} />
              ))}
            </div>
          </ChipErrorBoundary>
        )}
        {/* V9.44.3 — All chat-bubble side-cards live behind error */}
        {/* boundaries so a render bug in any one of them (malformed */}
        {/* lookup payload, missing field, etc.) fails gracefully */}
        {/* with a tiny inline pill instead of blacking out the */}
        {/* whole conversation route. */}
        {/* V9.26 — live-data badges for any realtime tool calls in this turn */}
        {!isUser && realtimeResults && realtimeResults.length > 0 && (
          <ChipErrorBoundary label="live-data">
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {realtimeResults.map((rt, idx) => (
                <LiveDataBadge key={idx} entry={rt} />
              ))}
            </div>
          </ChipErrorBoundary>
        )}
        {/* V9.44 — reminder lookup card (collapsed one-liner per match) */}
        {!isUser && reminderLookup && (
          <ChipErrorBoundary label="reminder-lookup">
            <ReminderLookupChip lookup={reminderLookup} />
          </ChipErrorBoundary>
        )}
        {/* Portrait reveal — when the LLM calls show_my_face */}
        {!isUser && showFace && (
          <div
            className="mt-2 rounded-2xl overflow-hidden border border-cyan-500/20 max-w-[260px] shadow-lg shadow-cyan-500/5"
            data-testid="show-face-portrait"
          >
            <img
              src="/journey-avatar-512.png"
              alt="Journey's portrait"
              className="w-full h-auto block"
              draggable={false}
            />
          </div>
        )}
        {/* Step-by-step visual guide produced via the generate_step_guide tool */}
        {!isUser && stepGuide && (
          <ChipErrorBoundary label="step-guide">
            <div className="w-full">
              <StepGuideCards
                guide={stepGuide}
                messageId={message.id}
                status={stepStatus}
                onRegenerate={
                  onRegenerateStep
                    ? (idx) => onRegenerateStep(message.id, idx)
                    : null
                }
              />
            </div>
          </ChipErrorBoundary>
        )}
        {inlineSuggestion && (
          <InlineMemoryChip
            suggestion={inlineSuggestion}
            onResolved={onInlineResolved}
          />
        )}
        {/* Timestamp footer — fixed-height slot so streaming doesn't cause layout shift */}
        <div
          className={`mt-1 h-4 flex items-center text-[10.5px] text-white/35 ${
            isUser ? "justify-end pr-1" : "pl-1"
          }`}
          data-testid={`message-timestamp-${message.id}`}
        >
          {!isStreaming && message.created_at && (
            <span>{formatChatTimestamp(message.created_at, tz)}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex gap-3" data-testid="typing-indicator">
      <JourneyAvatar size={32} ringed={false} testId="typing-avatar" />
      <div className="rounded-2xl px-4 py-3 bg-[#1a1a1a] border border-white/5">
        <div className="flex gap-1.5 items-center h-5">
          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-cyan-400" />
          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-cyan-400" />
          <span className="typing-dot w-1.5 h-1.5 rounded-full bg-cyan-400" />
        </div>
      </div>
    </div>
  );
}
